import { useEffect, useState, type FC } from "react";
import type { ClientInvoiceReaderTemplate } from "../../../models/ClientInvoiceReaderTemplateModel";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { SendIcon } from "../../atoms/icons/SendIcon";
import { theme } from "../../../theme";
import Modal from "../Modal";

import { HorizontalSpacer } from "../../atoms/Spacers";
import Select from "../../atoms/inputs/Select";
import ActionButton from "../../atoms/buttons/ActionButton";
import { useSendMultipleForInterpretationWithTemplate } from "../../../graphql/hooks/documentHandler/useSendMultipleForInterpretationWithTemplate";
import Alert from "../Alert";
import { OpenLinkIcon } from "../../atoms/icons/OpenLinkIcon";
import { useNavigate } from "react-router-dom";
import Tooltip from "../Tooltip";
import Body from "../../atoms/typography/Body";
import IconButton from "../../atoms/buttons/IconButton";
import { useTranslation } from "react-i18next";
import BodySmall from "../../atoms/typography/BodySmall";
import { CrossIcon } from "../../atoms/icons/CrossIcon";

interface Props {
  clientTemplates?: Array<ClientInvoiceReaderTemplate>;
  documentsForInterpretation?: Array<{
    uploadUnitId: string;
    uploadDocumentIds: Array<string>;
  }>;
  variant: "small" | "medium" | "iconButton";
  clientIdentifier: string;
  selectedClientId?: string;
  onClearSelection: () => void;
  selectedDocuments: Array<string>;
}

const InterpretWithTemplateButton: FC<Props> = ({
  clientTemplates,
  documentsForInterpretation,
  variant,
  clientIdentifier,
  selectedClientId,
  onClearSelection,
  selectedDocuments,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [length, setLength] = useState(selectedDocuments.length);

  useEffect(() => {
    setLength(selectedDocuments.length);
  }, [selectedDocuments]);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | undefined>(
    undefined,
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [redirectUri, setRedirectUri] = useState("");
  const [successResponse, setSuccessResponse] = useState<
    | undefined
    | Array<{
        documentId: string;
        interpretationId: string;
        isSuccessful: boolean;
      }>
  >(undefined);

  const getTemplateOptionsForClient = () => {
    if (!clientTemplates) return [];
    const templatesThatAreActive = clientTemplates.filter(
      (t) => t.state === "ACTIVE",
    );
    const existingTemplates = templatesThatAreActive.map((t) => ({
      label: t.name,
      value: t.id,
    }));

    const existingTemplatesSorted = existingTemplates.sort((a, b) =>
      a.label.localeCompare(b.label),
    );
    return [
      { label: t("documentHandler.selectDynamic"), value: undefined },
      ...existingTemplatesSorted,
    ];
  };

  const onCompletedInterpretationWithTemplate = (result: any) => {
    if (
      result?.documentHandler?.sendMultipleForInterpretationWithTemplate
        ?.success === true
    ) {
      setErrorMessage("");
      setSuccessMessage(t("documentHandler.interpretWithTemplateSuccessful"));

      const results =
        result?.documentHandler?.sendMultipleForInterpretationWithTemplate
          ?.results;
      setSuccessResponse(results);
      const objectsToBase64: Array<{ fileName: string; id: string }> =
        results.map((r: { documentId: string; interpretationId: string }) => ({
          fileName:
            documentsForInterpretation?.find(
              (sd) => sd.uploadUnitId === r.documentId,
            ) ?? "unknown",
          id: r.interpretationId,
        }));
      const objectsAsBase64 = btoa(JSON.stringify(objectsToBase64));
      const redirectUrl = `/interpretationResult/client/${clientIdentifier}/${objectsAsBase64}`;
      setRedirectUri(redirectUrl);

      onClearSelection();
    } else {
      setSuccessMessage("");
      setErrorMessage(t("errors.somethingWentWrong"));
    }
  };
  const onErrorInterpretationWithTemplate = () => {
    setErrorMessage(t("errors.somethingWentWrong"));
  };
  const { interpretMultipleWithTemplate, loading, error } =
    useSendMultipleForInterpretationWithTemplate({
      onCompleted: onCompletedInterpretationWithTemplate,
      onError: onErrorInterpretationWithTemplate,
    });

  const onClickInterpretationWithTemplate = async () => {
    try {
      await interpretMultipleWithTemplate({
        variables: {
          input: {
            templateId: selectedTemplate,
            uploadUnitsWithDocumentsArray: documentsForInterpretation,
          },
          clientIdentifier: clientIdentifier,
        },
      });
    } catch (e: any) {
      setErrorMessage(e?.toString() ?? t("errors.somethingWentWrong"));
    }
  };

  return (
    <div>
      {variant === "iconButton" ? (
        <Tooltip
          content={
            <Body color={theme.colors.white}>
              {t("documentHandler.interpretWithTemplate")}
            </Body>
          }
        >
          <IconButton
            icon={<SendIcon />}
            onClick={(e?: React.MouseEvent) => {
              e?.stopPropagation();
              setModalOpen(true);
            }}
          />
        </Tooltip>
      ) : (
        <OutlinedButton
          title={`${t("documentHandler.interpretWithTemplate")} (${
            clientIdentifier === selectedClientId ? length : 0
          }st)`}
          icon={<SendIcon color={theme.colors.primary} />}
          medium={variant === "medium" ? true : false}
          color={
            (clientTemplates && clientTemplates?.length < 1) ||
            !clientTemplates ||
            length < 1
              ? theme.colors.white
              : theme.colors.primary
          }
          disabled={
            (clientTemplates && clientTemplates?.length < 1) ||
            !clientTemplates ||
            length < 1 ||
            clientIdentifier !== selectedClientId
          }
          onClick={(e?: React.MouseEvent) => {
            e?.stopPropagation();
            setModalOpen(true);
          }}
        />
      )}

      {modalOpen && (
        <Modal
          onClose={() => setModalOpen(false)}
          closeIcon
          title={t("documentHandler.interpretWithTemplate")}
        >
          <Select
            label={t("documentHandler.selectTemplate")}
            options={getTemplateOptionsForClient()}
            onChange={(value) => setSelectedTemplate(value)}
          />
          <HorizontalSpacer spacing={2} />
          {errorMessage && (
            <Alert
              type="error"
              title={errorMessage}
              message={error?.toString()}
            />
          )}
          {successMessage && <Alert type="success" title={successMessage} />}
          {successResponse && (
            <div>
              {successResponse
                .filter((x) => x.isSuccessful === false)
                ?.map((result) => (
                  <div key={result.documentId}>
                    <BodySmall fontweight={500}>{t("menu.invoices")}</BodySmall>
                    <div
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      <CrossIcon color={theme.colors.error} />
                      <BodySmall>
                        {t(
                          "documentHandler.documentCouldNotBeSentForInterpretation",
                          {
                            documentId: result.documentId,
                          },
                        )}
                      </BodySmall>
                    </div>
                  </div>
                ))}
            </div>
          )}

          <div>
            <HorizontalSpacer spacing={1} />
            {redirectUri ? (
              <ActionButton
                title={t("documentHandler.showInterpretationResult")}
                icon={<OpenLinkIcon color={theme.colors.white} />}
                color={theme.colors.primary}
                onClick={() => navigate(redirectUri)}
              />
            ) : (
              <ActionButton
                title={t("documentHandler.interpretWithTemplate")}
                icon={<SendIcon color={theme.colors.white} />}
                color={theme.colors.primary}
                loading={loading}
                onClick={onClickInterpretationWithTemplate}
              />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default InterpretWithTemplateButton;
